import React, { useEffect, useState } from "react";
import { X, Eye, User, Clock, UsersRound } from "lucide-react";
import api from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "hace unos segundos";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
};

/**
 * Modal for the story owner to see who watched a particular story.
 *
 * Props:
 *   storyId   string
 *   onClose() -> void
 */
const StoryViewsModal = ({ storyId, onClose }) => {
  const [data, setData] = useState({ items: [], total_views: 0, unique_views: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get(`/stories/${storyId}/views`)
      .then(({ data }) => live && setData(data))
      .catch((e) => live && setError(e?.response?.data?.detail || "No se pudieron cargar las vistas."))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [storyId]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={onClose}
      data-testid="story-views-modal"
    >
      <div
        className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <span className="w-10 h-10 rounded-full bg-orange/10 text-orange flex items-center justify-center">
            <Eye size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-orange">Vistas</p>
            <h3 className="font-display text-xl text-teal-deep">Quién vio tu historia</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-cream hover:bg-orange hover:text-white text-teal-deep flex items-center justify-center"
            aria-label="Cerrar"
            data-testid="story-views-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 bg-cream rounded-full px-3 py-1 font-semibold text-teal-deep">
            <Eye size={12} /> {data.total_views} totales
          </span>
          <span className="inline-flex items-center gap-1 bg-cream rounded-full px-3 py-1 font-semibold text-teal-deep">
            <UsersRound size={12} /> {data.unique_views} únicos
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-3">
          {loading ? (
            <p className="text-center text-teal-soft py-8 text-sm">Cargando…</p>
          ) : error ? (
            <p className="text-center text-red-600 py-8 text-sm">{error}</p>
          ) : data.items.length === 0 ? (
            <div className="text-center py-10 text-teal-soft" data-testid="story-views-empty">
              <Eye size={28} className="mx-auto mb-2 text-gray-300" />
              <p className="font-semibold text-sm">Aún nadie ha visto esta historia</p>
              <p className="text-xs mt-1">Compártela por WhatsApp para llegar a más gente.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.items.map((v) => (
                <li key={v.id} className="flex items-center gap-3 px-3 py-3" data-testid={`story-view-${v.id}`}>
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center ${v.is_guest ? "bg-gray-100 text-gray-500" : "bg-teal/10 text-teal"}`}>
                    <User size={16} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-teal-deep truncate">
                      {v.is_guest ? "Visitante anónimo" : (v.viewer_name || "Miembro")}
                    </p>
                    <p className="text-xs text-teal-soft inline-flex items-center gap-1">
                      <Clock size={10} /> {timeAgo(v.viewed_at)}
                    </p>
                  </div>
                  {!v.is_guest && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-teal/10 text-teal rounded-full px-2 py-0.5">
                      Miembro
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryViewsModal;
